import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';

@Component({
  selector: 'app-qr-code',
  templateUrl: './qr-code.component.html',
  styleUrls: ['./qr-code.component.scss'],
})
export class QrCodeComponent implements OnDestroy {
  @ViewChild('video', { static: false }) video!: ElementRef<HTMLVideoElement>;

  @Output() scanSuccess = new EventEmitter<string>();
  @Output() scannerClosed = new EventEmitter<void>();

  private codeReader = new BrowserMultiFormatReader();
  private controls?: IScannerControls;
  private activeStream?: MediaStream;

  /** Estado visível no template */
  permissionDenied = false;
  cameraError = '';
  isLoading = true;

  async ngAfterViewInit() {
    setTimeout(() => this.startScanner(), 300);
  }

  isMobile(): boolean {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  /**
   * Solicita permissão de câmera explicitamente antes de iniciar o scanner.
   * Isso garante que o prompt de permissão apareça em PWAs (Android e iOS).
   */
  private async requestCameraPermission(): Promise<MediaStream> {
    const constraints: MediaStreamConstraints = {
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920, min: 1280 },
        height: { ideal: 1080, min: 720 },
      } as MediaTrackConstraints,
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return stream;
    } catch (err: any) {
      if (
        err.name === 'NotAllowedError' ||
        err.name === 'PermissionDeniedError'
      ) {
        this.permissionDenied = true;
        throw new Error('PERMISSION_DENIED');
      }

      // Fallback com constraints mínimas caso o device não suporte resolução alta
      return navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
    }
  }

  /**
   * Aplica configurações avançadas na track de vídeo:
   * - focusMode contínuo (autofoco)
   * - zoom (se suportado pelo device)
   */
  private async applyAdvancedConstraints(stream: MediaStream): Promise<void> {
    const track = stream.getVideoTracks()[0];
    if (!track) return;

    try {
      const capabilities = track.getCapabilities?.() as any;
      const advancedConstraints: any = {};

      if (capabilities?.focusMode?.includes('continuous')) {
        advancedConstraints.focusMode = 'continuous';
      }

      if (capabilities?.zoom) {
        const minZoom = capabilities.zoom.min || 1;
        const maxZoom = capabilities.zoom.max || 1;
        const targetZoom = Math.min(
          minZoom + (maxZoom - minZoom) * 0.3,
          maxZoom,
        );
        advancedConstraints.zoom = targetZoom;
      }

      if (Object.keys(advancedConstraints).length > 0) {
        await track.applyConstraints({
          advanced: [advancedConstraints],
        } as any);
      }
    } catch (e) {
      console.warn('Não foi possível aplicar constraints avançadas:', e);
    }
  }

  async startScanner() {
    this.isLoading = true;
    this.permissionDenied = false;
    this.cameraError = '';

    try {
      // 1. Solicitar permissão explicitamente (resolve problema em PWAs)
      const permissionStream = await this.requestCameraPermission();
      permissionStream.getTracks().forEach((t) => t.stop());

      this.isLoading = false;

      // 2. Constraints com resolução alta para melhor foco
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
        } as MediaTrackConstraints,
      };

      // 3. Iniciar decodificação
      this.controls = await this.codeReader.decodeFromConstraints(
        constraints,
        this.video.nativeElement,
        (result: any, err: any) => {
          if (result) {
            const qr = result.getText();

            if (navigator.vibrate) {
              navigator.vibrate(200);
            }

            this.scanSuccess.emit(qr);
            this.stopScanner();
          }

          if (err && err.name !== 'NotFoundException') {
            console.error(err);
          }
        },
      );

      // 4. Após o scanner iniciar, aplicar foco contínuo e zoom na track ativa
      const videoEl = this.video.nativeElement;
      if (videoEl.srcObject instanceof MediaStream) {
        this.activeStream = videoEl.srcObject;
        await this.applyAdvancedConstraints(this.activeStream);
      }
    } catch (err: any) {
      this.isLoading = false;

      if (err?.message === 'PERMISSION_DENIED') {
        this.cameraError =
          'Permissão de câmera negada. Habilite nas configurações do navegador.';
      } else {
        this.cameraError =
          'Não foi possível acessar a câmera. Verifique as permissões.';
        console.error('Erro ao iniciar scanner:', err);
      }
    }
  }

  stopScanner() {
    if (this.controls) {
      this.controls.stop();
      this.controls = undefined;
    }

    if (this.activeStream) {
      this.activeStream.getTracks().forEach((track) => track.stop());
      this.activeStream = undefined;
    }

    if (this.video?.nativeElement) {
      this.video.nativeElement.srcObject = null;
    }

    this.scannerClosed.emit();
  }

  ngOnDestroy() {
    this.stopScanner();
  }
}
